import { useCallback, useEffect, useRef, useState } from 'react';
import { csrfFetch } from '../../lib/installCsrfAxios';

/**
 * useSiteChat — conversation state for the public site assistant.
 *
 * Owns four things the UI should not have to think about: the server config, the
 * message list, the SSE read loop, and a single explicit status the panel renders
 * from ('idle' | 'waiting' | 'streaming' | 'error').
 *
 * The distinction between 'waiting' and 'streaming' is the whole point of the
 * split — "we sent it, nothing has come back yet" and "words are arriving" are
 * different states to a visitor, and collapsing them into one spinner is what
 * makes a chat widget feel broken when the model takes a second to start.
 */

const STORAGE_KEY = 'rts-site-chat-v1';
// Versioned: a cached config from an older deploy has an older shape, and reading
// it back would silently drop whatever the new field is.
const CONFIG_KEY = 'rts-site-chat-config-v2';
const MAX_STORED = 40;

const ERRORS = {
  rate_limited: "That's a lot of questions in a short time. Give it a minute, or email kris@rivieratech.solutions.",
  unconfigured: 'The assistant is offline right now. The contact page still reaches us directly.',
  upstream_busy: 'Busy right now — try that again in a moment.',
  upstream_error: "Something broke on our side. Try again, or use the contact page and we'll reply personally.",
  network: 'Lost the connection. Check your network and try again.',
  aborted: null, // visitor pressed stop; not an error to show
};

let idCounter = 0;
const nextId = () => `m${Date.now().toString(36)}${(idCounter += 1)}`;

function loadStored(key, fallback) {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function store(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — the conversation just won't survive a reload */
  }
}

export default function useSiteChat() {
  const [config, setConfig] = useState(() => loadStored(CONFIG_KEY, null));
  const [messages, setMessages] = useState(() => loadStored(STORAGE_KEY, []));
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Config decides whether the widget renders at all, so it is fetched once per
  // session and cached — a marketing page should not pay a round trip per view.
  useEffect(() => {
    if (config) return undefined;
    let cancelled = false;

    fetch('/api/site-chat/config', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.success) return;
        const next = {
          enabled: Boolean(data.enabled),
          greeting: data.greeting,
          suggestions: data.suggestions || [],
          maxLength: data.maxLength || 1200,
          linkPaths: data.linkPaths || [],
          linkLabels: data.linkLabels || {},
        };
        setConfig(next);
        store(CONFIG_KEY, next);
      })
      .catch(() => {
        // Offline or the endpoint is down. Leave config null → no bubble.
      });

    return () => {
      cancelled = true;
    };
  }, [config]);

  useEffect(() => {
    store(STORAGE_KEY, messages.slice(-MAX_STORED));
  }, [messages]);

  // Never leave a stream running behind an unmounted widget.
  useEffect(() => () => abortRef.current?.abort(), []);

  const patchLast = useCallback((patch) => {
    setMessages((prev) => {
      if (!prev.length) return prev;
      const next = prev.slice();
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, ...(typeof patch === 'function' ? patch(last) : patch) };
      return next;
    });
  }, []);

  /**
   * Send one turn. `history` is everything already exchanged — the server bounds
   * and re-sanitises it, so trimming here is only about not shipping a novel.
   */
  const run = useCallback(
    async (text) => {
      const controller = new AbortController();
      abortRef.current = controller;

      const history = messagesRef.current
        .filter((m) => m.status !== 'error' && m.content.trim())
        .slice(-14)
        .map((m) => ({ role: m.role, content: m.content }));

      setStatus('waiting');
      setError(null);
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'user', content: text, status: 'done' },
        { id: nextId(), role: 'assistant', content: '', status: 'pending' },
      ]);

      try {
        const res = await csrfFetch('/api/site-chat/stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify({ message: text, history }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          const code =
            res.status === 429 ? 'rate_limited' : res.status === 503 ? 'unconfigured' : 'upstream_error';
          throw Object.assign(new Error(code), { code });
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamed = '';
        let failure = null;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const frames = buffer.split('\n\n');
          buffer = frames.pop() || '';

          for (const frame of frames) {
            const line = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let evt;
            try {
              evt = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }

            if (evt.type === 'delta') {
              if (!streamed) setStatus('streaming'); // first token: waiting → streaming
              streamed += evt.v;
              patchLast({ content: streamed, status: 'streaming' });
            } else if (evt.type === 'error') {
              failure = evt.code;
            }
          }
        }

        if (failure && !streamed) throw Object.assign(new Error(failure), { code: failure });

        // A stream that ended with nothing in it is a failure the visitor can see,
        // so treat it as one rather than leaving an empty bubble on screen.
        if (!streamed.trim()) throw Object.assign(new Error('upstream_error'), { code: 'upstream_error' });

        patchLast({ content: streamed, status: 'done' });
        setStatus('idle');
      } catch (err) {
        const code = controller.signal.aborted ? 'aborted' : err.code || 'network';

        if (code === 'aborted') {
          // Keep whatever streamed in — a half answer the visitor chose to stop is
          // still theirs. Drop the bubble only if it never produced anything.
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant' && !last.content.trim()) return prev.slice(0, -1);
            return prev.map((m, i) => (i === prev.length - 1 ? { ...m, status: 'stopped' } : m));
          });
          setStatus('idle');
          return;
        }

        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && !last.content.trim()) return prev.slice(0, -1);
          return prev;
        });
        setError({ code, message: ERRORS[code] || ERRORS.upstream_error, retry: text });
        setStatus('error');
      } finally {
        abortRef.current = null;
      }
    },
    [patchLast]
  );

  const send = useCallback(
    (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || status === 'waiting' || status === 'streaming') return;
      run(trimmed.slice(0, config?.maxLength || 1200));
    },
    [run, status, config]
  );

  const retry = useCallback(() => {
    if (!error?.retry) return;
    // Drop the failed user turn — run() re-adds it, and two copies of the same
    // question in the transcript reads as a bug.
    setMessages((prev) => {
      const idx = prev.map((m) => m.role).lastIndexOf('user');
      return idx === -1 ? prev : prev.slice(0, idx);
    });
    const text = error.retry;
    setError(null);
    run(text);
  }, [error, run]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setError(null);
    setStatus('idle');
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clean up */
    }
  }, []);

  return { config, messages, status, error, send, retry, stop, reset };
}
